import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NavigationPrompt from 'react-router-navigation-prompt';
import { useResponsive } from '../../../../hooks/useResponsive';
import {
  useDisableSubmit,
} from '../../../changeCoverage/changeCoverageOptions/useDisableSubmit';
import { possesiveAdaptationI18n } from '../../../../services/utils';

import { useCoverageOptions } from '../hooks/useCoverageOptions';

import { useCoverageModal } from '../hooks/useCoverageModal';

import {
  DeductibleReimbursement,
} from '../../../changeCoverage/sliders/DeductibleReimbursement';
import {
  PetCopayCoverageSection,
} from '../../../changeCoverage/changeCoverageOptions/PetCopayCoverageSection';
import {
  CardPowerupSection,
} from '../../../changeCoverage/powerups/CardPowerupSection';
import {
  ReviewChangesModal,
} from '../../../changeCoverage/reviewChangesModal/ReviewChangesModal';
import {
  RateErrorModal,
} from '../../../changeCoverage/changeCoverageOptions/RateErrorModal';
import {
  SubmitChanges,
} from '../../../changeCoverage/changeCoverageOptions/SubmitChanges';
import {
  ChangeCoveragePreviewSubmit,
  ChangeCoverageSubmitted,
} from '../changeCoverageSubmit';
import { ChangeCoverageScreenFooter } from './ChangeCoverageScreenFooter';
import { ModifierModalInfo } from '../../modifiers/ModifierModalInfo';
import FigoLoadingOverlay from '../../../common/FigoLoadingOverlay';
import UnsavedChangesPrompt from '../../../common/UnsavedChangesPrompt';

import styles from '../css/ChangeCoverageScreenOptions.module.css';

import { PowerUpCardWithCheckboxes } from '../prop103/PowerUpCardWithCheckboxes';
import { UpgradePlanCard } from '../prop103/UpgradePlanCard';
import { usePetcloudCCProp103, useSuccessScreenChangeCoverage } from '../../../../hooks';

import { prop103 } from './prop103-data';
import { ChangeCoverageCoverageModal } from './ChangeCoverageCoverageModal';
import { ChangeCoverageWarning } from './ChangeCoverageWarning';
import { formatDate } from '../../coverageOverview/changeCoverage.utils';

const ChangeCoverageScreenOptions = ({
  onRateCreationError = () => { },
  policyNumber = '',
  validatePromptWarning = () => true,
}) => {
  const { t } = useTranslation('changeCoverage');
  const { isMobile } = useResponsive();
  const disableSubmit = useDisableSubmit();
  const useCCProp103 = usePetcloudCCProp103();
  const {
    cleanUpRate,
    deductibleLegend,
    effectiveDate,
    handleAddPowerup,
    handleCloseMultiModal,
    handleCloseSubmit,
    handleErrorModalClose,
    handleGoBack,
    handlePetProtectCopayInfoClick,
    handleReviewChangesClick,
    handleVetFeesClick,
    handleWellnessClick,
    insuranceProduct,
    loading,
    loadingSubmit,
    multiModalCategoryId,
    multimodalDataSelected,
    multiModalVisible,
    onChangeCoverageType,
    onReviewChangesClosed,
    petInfo,
    petProtectCopayModifier,
    powerUpLoadings,
    quoteSelectedValues,
    rateErrorModalVisible,
    reviewChangesVisible,
    shouldPromptWarning,
    success,
  } = useCoverageOptions({
    onRateCreationError,
    policyNumber,
    validatePromptWarning,
  });

  const { wellnessBenefits } = useCoverageModal();
  const ChangeCoverageSubmittedFlag = useSuccessScreenChangeCoverage();
  const successfulSubmitScreen = success && ChangeCoverageSubmittedFlag;

  const defaultCoverageType = useRef();
  const [planSelected, setPlanSelected] = useState(0);
  const [showCoverageModal, setCoverageModal] = useState(false);

  const handleSelectCoverageType = useCallback(({ id, title }) => () => {
    if (id === planSelected) {
      return;
    }

    onChangeCoverageType({ id, title });
    setPlanSelected(id);
    setCoverageModal(id !== defaultCoverageType.current.id);
  }, [planSelected]);

  function renderNavigationPrompt(props) {
    const { onConfirm, onCancel } = props;

    return (
      <UnsavedChangesPrompt
        message={t('options.promptMessage')}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    );
  }

  const closeCoverageModal = () => setCoverageModal(false);

  const onConfirmCoverageModal = () => {
    setCoverageModal(false);
    setPlanSelected(defaultCoverageType.current.id);

    onChangeCoverageType(defaultCoverageType.current);
  };

  const renderPorwerUps = () => {
    if (loading) {
      return null;
    }

    if (useCCProp103) {
      return (
        <PowerUpCardWithCheckboxes
          bundleModifierAdd={handleAddPowerup}
          multiModalData={prop103.multiModalData}
          petType={petInfo.PetType}
          powerUpInfo={prop103.powerUpInfo}
          quoteSelected={quoteSelectedValues}
          selected={null}
          title={t('preventive')}
        />
      );
    }

    return (
      <CardPowerupSection
        handleWellnessClick={handleWellnessClick}
        insuranceProduct={insuranceProduct}
        multiModalData={multimodalDataSelected}
        onAddPowerup={handleAddPowerup}
        onVetFeesClick={handleVetFeesClick}
        petName={petInfo.petName}
        petQuote={petInfo}
        powerUpLoadings={powerUpLoadings}
        quoteSelected={quoteSelectedValues}
      />
    );
  };

  useEffect(() => {
    if (!defaultCoverageType.current) {
      setPlanSelected(quoteSelectedValues.coverageType);
      defaultCoverageType.current = {
        id: quoteSelectedValues.coverageType,
        title: quoteSelectedValues.coverageName,
      };
    }
  }, [quoteSelectedValues]);

  if (reviewChangesVisible) {
    return (
      <>
        <ChangeCoveragePreviewSubmit
          disableSubmit={disableSubmit}
          petInfo={petInfo}
          policyNumber={policyNumber}
        />

        <FigoLoadingOverlay visible={loadingSubmit} />

        <NavigationPrompt
          afterConfirm={cleanUpRate}
          disableNative={false}
          when={shouldPromptWarning}
        >
          {renderNavigationPrompt}
        </NavigationPrompt>
      </>
    );
  }

  // applies for all kind of products
  if (successfulSubmitScreen) {
    return <ChangeCoverageSubmitted />;
  }

  return (
    <>
      <div className={styles.container}>
        <h3>{t('options.optionsTitle')}</h3>

        <p className={styles.description}>
          {t('options.instruction', {
            count: possesiveAdaptationI18n(petInfo.Name),
            effDate: formatDate(effectiveDate),
            interpolation: {
              escapeValue: false,
            },
            name: petInfo.petName,
          })}
        </p>

        {!loading && useCCProp103 && (
          <>
            <div className="Change-coverage-upgradesContainer">
              {prop103?.planTypeInfo.map((plan) => (
                <UpgradePlanCard
                  key={plan.id}
                  coveredItems={plan.coveredItems}
                  description={plan.description}
                  icon={plan.icon}
                  id={plan.id}
                  isSelected={plan.id === planSelected}
                  monthly={plan.monthly}
                  onClick={handleSelectCoverageType(plan)}
                  plans={petInfo?.Plans || []}
                  // WIP prop 103 calculate price
                  // price={getPlanTypePrice(plan.id)}
                  price={999}
                  quoteSelected={quoteSelectedValues}
                  title={plan.title}
                />
              ))}
            </div>

            {defaultCoverageType.current
              && planSelected !== defaultCoverageType.current?.id
              && (
                <ChangeCoverageWarning
                  description={t('prop103.warningDescription')}
                  title={t('prop103.warningTitle')}
                />
              )}

            <div>
              <h3 className={styles.coverageTitle}>
                {t('prop103.customPlanTitle')}
              </h3>

              <p>
                {t('prop103.customPlanDescription', { name: petInfo.petName })}
              </p>
            </div>
          </>
        )}

        {!loading && (
          <DeductibleReimbursement
            deductibleLegend={deductibleLegend}
            plans={petInfo.Plans}
            quoteSelected={quoteSelectedValues}
          />
        )}

        {/* WIP prop103: use isProp (or similar) instead of useCCProp103
        on rate */}
        {!loading && petProtectCopayModifier && !useCCProp103 && (
          <PetCopayCoverageSection
            active={petProtectCopayModifier.IsSelected}
            howItWorksClick={handlePetProtectCopayInfoClick}
            modifierData={petProtectCopayModifier}
            onAddPowerup={handleAddPowerup}
            petQuote={petInfo}
          />
        )}

        {renderPorwerUps()}

        <FigoLoadingOverlay visible={loading || !!powerUpLoadings.length} />

        <ReviewChangesModal
          disableSubmit={disableSubmit}
          onClose={onReviewChangesClosed}
          petInfo={petInfo}
          policyNumber={policyNumber}
          showModal={reviewChangesVisible}
        />

        {multimodalDataSelected && (
          <ModifierModalInfo
            initialMenuSelected={multiModalCategoryId}
            isVisible={multiModalVisible}
            isWhatsCovered
            multiModalData={multimodalDataSelected}
            onClose={handleCloseMultiModal}
            wellnessBenefits={wellnessBenefits}
          />
        )}

        {!loading && (
          <RateErrorModal
            onClose={handleErrorModalClose}
            tErrorText={t}
            visible={rateErrorModalVisible}
          />
        )}

        <NavigationPrompt
          afterConfirm={cleanUpRate}
          disableNative={false}
          when={shouldPromptWarning}
        >
          {renderNavigationPrompt}
        </NavigationPrompt>

        <SubmitChanges
          isVisible={success && !isMobile && !ChangeCoverageSubmittedFlag}
          onClose={handleCloseSubmit}
        />
      </div>

      <ChangeCoverageScreenFooter
        handleGoBack={handleGoBack}
        onReviewChangesClick={handleReviewChangesClick}
      />

      <ChangeCoverageCoverageModal
        closeCoverageModal={closeCoverageModal}
        onConfirmCoverageModal={onConfirmCoverageModal}
        showCoverageModal={showCoverageModal}
      />
    </>
  );
};

export { ChangeCoverageScreenOptions };
